/**
 * ExtractBizLogicView — Extract Business Logic Stage
 *
 * Two-part stage:
 *   Part 1 (view === 'setup')    — configuration entry screen
 *   Part 2 (view === 'workbench') — three-column business logic workbench
 *
 * Backend wired 2026-05-04:
 *   "Generate Business Logic" calls /extractbizlogic, polls status,
 *   loads real results into workbench on success.
 *
 * Workbench tabs:
 *   'browse'      — three-column: Browser | Spec View | Source Context
 *   'application' — application-level summary cards + sections
 *   'file'        — file/program-level business spec
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import JSZip from 'jszip'
import {
  ArrowLeft, ChevronRight, ChevronDown, ChevronUp,
  Settings, HelpCircle, User, Home, Workflow, Plug,
  FileBarChart, BarChart2, LayoutGrid,
  FileText, BookOpen, Database, Code2, Network, Layers,
  Building2, Users, CreditCard, GitBranch, Zap, Globe,
  Search, Download, Eye, AlertCircle, X,
  ToggleLeft, ToggleRight, CheckCircle2, Circle,
  Play, RefreshCw, Package, Filter, Tag, Link,
  FolderOpen, ExternalLink, Sparkles, List, Loader,
} from 'lucide-react'
import { getAPIBaseUrl, isQA } from '../../environment'
import { startExtractBizLogic, waitForEBL } from '../../services/extractBizLogicService'
import './ExtractBizLogicView.css'
import '../runs/RunsView.css'

// ─── Rule type badge colours ───────────────────────────────────────────────────
const RULE_TYPE_COLORS = {
  Decision:      '#3b82f6',
  Validation:    '#f59e0b',
  Computation:   '#8b5cf6',
  Definitional:  '#6b7280',
  Action:        '#10b981',
  Process:       '#06b6d4',
  Policy:        '#ef4444',
  Authorization: '#f97316',
}

// ─── Static browser tree (used before results load) ───────────────────────────
const EMPTY_BROWSER_TREE = [
  {
    id: 'app-spec',
    type: 'application',
    icon: Building2,
    label: 'Application Business Specification',
    children: null,
  },
  {
    id: 'domains',
    type: 'group',
    icon: Layers,
    label: 'Business Domains',
    count: 0,
    children: [],
  },
  {
    id: 'programs',
    type: 'group',
    icon: FileText,
    label: 'Programs / Files',
    count: 0,
    children: [],
  },
]

// ─── Spec sections ────────────────────────────────────────────────────────────
const SPEC_SECTIONS = [
  { id: 'overview',     label: 'Overview',                    icon: FileText    },
  { id: 'purpose',      label: 'Business Purpose',            icon: Zap         },
  { id: 'capabilities', label: 'Key Capabilities',            icon: CheckCircle2 },
  { id: 'flows',        label: 'Main Flows / Transactions',   icon: GitBranch   },
  { id: 'rules',        label: 'Business Rules',              icon: BookOpen    },
  { id: 'data',         label: 'Data Used',                   icon: Database    },
  { id: 'external',     label: 'External Interactions',       icon: Globe       },
  { id: 'supporting',   label: 'Supporting Programs',         icon: Layers      },
  { id: 'risks',        label: 'Risks / Ambiguities',         icon: AlertCircle },
  { id: 'grounding',    label: 'Source Grounding',            icon: Link        },
]

// ─── Export: markdown builders ────────────────────────────────────────────────

function _mdApplicationSpec(results, appName) {
  const ov   = results.application_overview  || {}
  const sp   = results.program_specs         || {}
  const m    = results.ebl_metrics           || {}
  const date = new Date().toISOString().slice(0, 10)

  const domains = (ov.domains_identified || []).map(d => `- ${typeof d === 'string' ? d : d.name}`).join('\n') || '_None identified_'
  const flows   = (ov.transaction_flows  || []).map(f => {
    const steps = (f.steps || []).join(' → ')
    return `### ${f.name || 'Flow'}\n${f.description || ''}\n\n**Steps:** ${steps}`
  }).join('\n\n') || '_None identified_'
  const integrations = (ov.external_integrations || []).map(i => `- ${i}`).join('\n') || '_None_'
  const risks = (sp.risks || []).map(r => `- ${r}`).join('\n') || '_None flagged_'

  return `# Application Business Specification: ${appName}

_Generated by RecodeIT Extract Business Logic · ${date}_

---

## Overview

${ov.summary || sp.business_purpose || '_No summary available_'}

---

## Metrics

| Metric | Value |
|---|---|
| Domains Identified | ${m.domains_identified ?? '—'} |
| Programs Analyzed | ${m.programs_analyzed ?? '—'} |
| Transactions Identified | ${m.transactions_identified ?? '—'} |
| Rules Extracted | ${m.rules_extracted ?? '—'} |
| External Integrations | ${m.external_integrations ?? '—'} |

---

## Business Domains

${domains}

---

## Key End-to-End Flows

${flows}

---

## External Integrations

${integrations}

---

## Risks / Ambiguities

${risks}
`
}

function _mdBusinessRules(results, appName) {
  const catalog = results.business_rules_catalog || {}
  const rules   = catalog.rules || []
  const date    = new Date().toISOString().slice(0, 10)

  const tableRows = rules.map(r =>
    `| ${r.rule_id} | ${r.paragraph || '—'} | ${r.type} | ${(r.description || '').replace(/\|/g, '\\|')} | ${Math.round((r.confidence || 0) * 100)}% |`
  ).join('\n')

  const details = rules.map(r => `### ${r.rule_id} — ${r.type}

**Paragraph:** \`${r.paragraph || 'N/A'}\`
**Program:** ${r.program || '—'}

${r.description || ''}

${r.acceptance_criteria && r.acceptance_criteria !== '(Requires AI extraction for full detail.)'
  ? `**Acceptance Criteria:** _${r.acceptance_criteria}_`
  : ''
}

**Inputs:** ${(r.inputs || []).join(', ') || '—'}
**Outputs/State:** ${(r.outputs_state || []).join(', ') || '—'}
**Confidence:** ${Math.round((r.confidence || 0) * 100)}%
`).join('\n---\n\n')

  return `# Business Rules Catalog: ${appName}

_Generated by RecodeIT Extract Business Logic · ${date}_

---

## Summary Table

| Rule ID | Paragraph | Type | Description | Confidence |
|---|---|---|---|---|
${tableRows}

---

## Detailed Rules

${details || '_No rules extracted_'}
`
}

function _mdProgramSpec(results, appName) {
  const sp   = results.program_specs || {}
  const ov   = results.application_overview || {}
  const prog = (ov.programs_analyzed || [])[0] || appName
  const date = new Date().toISOString().slice(0, 10)
  const gr   = sp.source_grounding || {}
  const intel = gr.intelligence || {}

  const steps   = (sp.functional_steps || []).map((s, i) => `${i + 1}. ${s}`).join('\n') || '_None_'
  const files   = (sp.files_accessed   || []).map(f => `- ${typeof f === 'string' ? f : f.name}${f.organization ? ` (${f.organization})` : ''}`).join('\n') || '_None_'
  const called  = (sp.called_programs  || []).map(p => `- ${p}`).join('\n') || '_None_'
  const groups  = (sp.functional_groups || []).map(g => {
    const paras = (g.paragraphs || []).join(', ')
    return `**${g.name}**: ${g.purpose || ''}\n  Paragraphs: ${paras}`
  }).join('\n\n') || '_None_'
  const risks   = (sp.risks || []).map(r => `- ${r}`).join('\n') || '_None flagged_'

  return `# Program Specification: ${prog}

_Application: ${appName} · Generated by RecodeIT Extract Business Logic · ${date}_

---

## Business Purpose

${sp.business_purpose || '_Not available_'}

---

## Functional Steps

${steps}

---

## Files / Datasets

${files}

---

## Called Programs

${called}

---

## Transaction Grouping

${groups}

---

## Source Grounding

| Metric | Value |
|---|---|
| Paragraphs Analyzed | ${gr.paragraph_count ?? '—'} |
| Data Fields | ${gr.data_field_count ?? '—'} |
| Conditions (88-level) | ${gr.condition_count ?? '—'} |
| Total Lines | ${intel.total_lines ?? '—'} |
| Cyclomatic Complexity | ${intel.complexity ?? intel.cyclomatic_complexity ?? '—'} |
| LCOM4 | ${intel.lcom4 ?? '—'} |
| Fan-Out | ${intel.fan_out ?? '—'} |
| Instability | ${intel.instability != null ? Number(intel.instability).toFixed(2) : '—'} |

---

## Risks / Ambiguities

${risks}
`
}

/** Build all 3 markdown documents from loaded eblResults. Returns [{name, content}]. */
function buildMarkdownFiles(results, appName) {
  const safe = (appName || 'app').replace(/[^a-z0-9_-]/gi, '_')
  return [
    { name: `${safe}_application_business_spec.md`, desc: 'Application-level overview, domains, and flows', content: _mdApplicationSpec(results, appName) },
    { name: `${safe}_business_rules_catalog.md`,    desc: 'All extracted business rules with acceptance criteria', content: _mdBusinessRules(results, appName)  },
    { name: `${safe}_program_specification.md`,     desc: 'Program-level spec, steps, files, grounding',    content: _mdProgramSpec(results, appName)    },
  ]
}

/** Trigger a browser download from a string blob. */
function _triggerDownload(content, filename, mime = 'text/markdown') {
  const blob = new Blob([content], { type: mime })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildBrowserTree(results) {
  if (!results) return EMPTY_BROWSER_TREE
  const overview  = results.application_overview  || {}
  const domains   = overview.domains_identified   || []
  const programs  = overview.programs_analyzed    || []

  return [
    {
      id: 'app-spec',
      type: 'application',
      icon: Building2,
      label: 'Application Business Specification',
      children: null,
    },
    {
      id: 'domains',
      type: 'group',
      icon: Layers,
      label: 'Business Domains',
      count: domains.length,
      children: domains.map((d, i) => ({
        id: `domain-${i}`,
        type: 'domain',
        icon: Users,
        label: typeof d === 'string' ? d : d.name || `Domain ${i + 1}`,
      })),
    },
    {
      id: 'programs',
      type: 'group',
      icon: FileText,
      label: 'Programs / Files',
      count: programs.length,
      children: programs.map((p, i) => ({
        id: `program-${i}`,
        type: 'program',
        icon: FileText,
        label: typeof p === 'string' ? p : p.name || `Program ${i + 1}`,
      })),
    },
  ]
}

function buildMetrics(results) {
  const m = results?.ebl_metrics || {}
  return [
    { label: 'Domains Identified',      value: m.domains_identified      ?? '—', icon: Layers    },
    { label: 'Programs Analyzed',        value: m.programs_analyzed       ?? '—', icon: FileText  },
    { label: 'Transactions Identified',  value: m.transactions_identified ?? '—', icon: GitBranch },
    { label: 'Rules Extracted',          value: m.rules_extracted         ?? '—', icon: BookOpen  },
    { label: 'External Integrations',    value: m.external_integrations   ?? '—', icon: Globe     },
  ]
}

function getSpecContent(sectionId, results) {
  if (!results) return null
  const specs    = results.program_specs         || {}
  const overview = results.application_overview  || {}
  const catalog  = results.business_rules_catalog || {}

  switch (sectionId) {
    case 'overview':
      return overview.summary || specs.business_purpose || null
    case 'purpose':
      return specs.business_purpose || null
    case 'capabilities':
      return specs.functional_steps?.length ? specs.functional_steps : null
    case 'flows':
      return overview.transaction_flows?.length ? overview.transaction_flows : null
    case 'rules':
      return catalog.rules?.length ? catalog.rules : null
    case 'data':
      return specs.files_accessed?.length ? specs.files_accessed : null
    case 'external':
      return specs.called_programs?.length ? specs.called_programs : null
    case 'supporting':
      return specs.copybooks?.length ? specs.copybooks : null
    case 'risks':
      return specs.risks?.length ? specs.risks : null
    case 'grounding': {
      const gr = specs.source_grounding
      return gr && Object.keys(gr).length ? gr : null
    }
    default:
      return null
  }
}

// ─── Section content renderers ────────────────────────────────────────────────

function renderTextContent(text) {
  return <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--text-primary, #e5e7eb)', fontSize: 13 }}>{text}</p>
}

function renderListContent(items) {
  return (
    <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-primary, #e5e7eb)', fontSize: 13, lineHeight: 1.7 }}>
      {items.map((item, i) => (
        <li key={i}>{typeof item === 'string' ? item : item.name || JSON.stringify(item)}</li>
      ))}
    </ul>
  )
}

function renderFlowsContent(flows) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {flows.map((flow, i) => (
        <div key={i} style={{ padding: '8px 10px', background: 'var(--surface-2, rgba(255,255,255,0.04))', borderRadius: 6, border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-primary, #e5e7eb)', marginBottom: 4 }}>
            {flow.name || `Flow ${i + 1}`}
          </div>
          {flow.description && (
            <div style={{ fontSize: 12, color: 'var(--text-muted, #9ca3af)', marginBottom: 6 }}>{flow.description}</div>
          )}
          {flow.steps?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {flow.steps.map((step, si) => (
                <span key={si} style={{ fontSize: 11, padding: '2px 7px', background: 'var(--surface-3, rgba(255,255,255,0.08))', borderRadius: 3, color: 'var(--text-secondary, #d1d5db)', fontFamily: 'monospace' }}>
                  {step}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function renderRulesContent(rules) {
  const typeColors = RULE_TYPE_COLORS
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rules.map((rule, i) => (
        <div key={i} style={{ padding: '8px 10px', background: 'var(--surface-2, rgba(255,255,255,0.04))', borderRadius: 6, border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #9ca3af)', fontFamily: 'monospace' }}>{rule.rule_id}</span>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
              background: (typeColors[rule.type] || '#6b7280') + '22',
              color: typeColors[rule.type] || '#9ca3af',
              border: `1px solid ${(typeColors[rule.type] || '#6b7280')}44`,
            }}>
              {rule.type}
            </span>
            {rule.paragraph && (
              <span style={{ fontSize: 10, color: 'var(--text-muted, #9ca3af)', fontFamily: 'monospace', marginLeft: 'auto' }}>{rule.paragraph}</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-primary, #e5e7eb)', lineHeight: 1.5, marginBottom: 4 }}>{rule.description}</div>
          {rule.acceptance_criteria && rule.acceptance_criteria !== '(Requires AI extraction for full detail.)' && (
            <div style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', fontStyle: 'italic', borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))', paddingTop: 4, marginTop: 4 }}>
              {rule.acceptance_criteria}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function renderFilesContent(files) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}>
          <span style={{ fontFamily: 'monospace', color: 'var(--text-primary, #e5e7eb)', fontWeight: 500 }}>
            {typeof f === 'string' ? f : f.name || '—'}
          </span>
          {f.organization && <span style={{ color: 'var(--text-muted, #9ca3af)' }}>{f.organization}</span>}
          {f.access_mode && <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: 11 }}>{f.access_mode}</span>}
        </div>
      ))}
    </div>
  )
}

function renderGroundingContent(gr) {
  const rows = [
    { label: 'Paragraphs',   value: gr.paragraph_count },
    { label: 'Data Fields',  value: gr.data_field_count },
    { label: 'Conditions',   value: gr.condition_count },
    { label: 'Total Lines',  value: gr.intelligence?.total_lines },
    { label: 'LCOM4',        value: gr.intelligence?.lcom4 != null ? String(gr.intelligence.lcom4) : null },
    { label: 'MI Score',     value: gr.intelligence?.mi_score != null ? Number(gr.intelligence.mi_score).toFixed(1) : null },
  ].filter(r => r.value != null)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
      {rows.map(({ label, value }) => (
        <div key={label} style={{ padding: '6px 8px', background: 'var(--surface-2, rgba(255,255,255,0.04))', borderRadius: 5 }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted, #9ca3af)', marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #e5e7eb)' }}>{value}</div>
        </div>
      ))}
    </div>
  )
}

function renderSectionContent(sectionId, content) {
  if (!content) return null
  switch (sectionId) {
    case 'overview':
    case 'purpose':
      return renderTextContent(content)
    case 'capabilities':
    case 'supporting':
    case 'risks':
      return renderListContent(Array.isArray(content) ? content : [content])
    case 'flows':
      return renderFlowsContent(content)
    case 'rules':
      return renderRulesContent(content)
    case 'data':
      return renderFilesContent(content)
    case 'external':
      return renderListContent(Array.isArray(content) ? content : [content])
    case 'grounding':
      return renderGroundingContent(content)
    default:
      return renderTextContent(typeof content === 'string' ? content : JSON.stringify(content))
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

function ExtractBizLogicView({
  project,
  caJobId = null,
  accountId = null,
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToGenerateDocs,
  onGoToDecomposeCode,
  onGoToServiceGeneration,
  onGoToJavaPkg,
  onGoToRefineCode,
  onGoToRefactorCode,
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
}) {
  // ── View state
  const [view,          setView]          = useState('setup')      // 'setup' | 'workbench'
  const [workbenchTab,  setWorkbenchTab]  = useState('browse')     // 'browse' | 'application' | 'file'

  // ── Browser state
  const [selectedItem,   setSelectedItem]   = useState('app-spec')
  const [expandedGroups, setExpandedGroups] = useState(new Set(['domains']))
  const [browserSearch,  setBrowserSearch]  = useState('')

  // ── Spec state
  const [expandedSections, setExpandedSections] = useState(new Set(['overview', 'purpose', 'capabilities']))

  // ── Export panel
  const [showExport,    setShowExport]    = useState(false)
  const [exportFormat,  setExportFormat]  = useState('markdown')

  // ── Header menus
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  // ── Setup state
  const [scope,           setScope]           = useState('all')    // 'all' | 'selected'
  const [bizView,         setBizView]         = useState('both')   // 'app' | 'file' | 'both'
  const [includeSpec,     setIncludeSpec]     = useState(true)
  const [includeRules,    setIncludeRules]    = useState(true)
  const [includeTxGroups, setIncludeTxGroups] = useState(true)
  const [includeGrounding,setIncludeGrounding]= useState(true)

  // ── EBL job state (Phase 1 — backend wiring)
  const [eblLoading,   setEblLoading]   = useState(false)
  const [eblError,     setEblError]     = useState(null)
  const [eblProgress,  setEblProgress]  = useState('')     // 'queued' | 'running' | 'completed'
  const [eblResults,   setEblResults]   = useState(null)   // full results from /extractbizlogic/{id}/results

  // ── Export state
  const [exportBusy, setExportBusy] = useState(false)

  // ── CA job self-resolution — makes EBL navigation-order-independent
  // If caJobId prop is missing (user navigated here without passing it), resolve
  // the latest completed CA job for this project from the API.
  const [resolvedCaJobId,  setResolvedCaJobId]  = useState(null)
  const [caJobResolving,   setCaJobResolving]   = useState(false)

  useEffect(() => {
    if (caJobId || !project?.account || !project?.name) return
    // Prop absent — attempt live discovery
    setCaJobResolving(true)
    const token   = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const url = `${getAPIBaseUrl()}/codeanalysis/latest-job`
              + `?account_id=${encodeURIComponent(project.account)}`
              + `&app_name=${encodeURIComponent(project.name)}`
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.job_id) {
          console.log('[EBL] caJobId prop missing — resolved via latest-job:', data.job_id)
          setResolvedCaJobId(data.job_id)
        } else {
          console.log('[EBL] No CA job found for project:', project.name)
        }
      })
      .catch(err => console.warn('[EBL] latest-job lookup failed:', err.message))
      .finally(() => setCaJobResolving(false))
  }, [caJobId, project?.account, project?.name])  // eslint-disable-line react-hooks/exhaustive-deps

  // Use prop first; fall back to self-resolved value
  const effectiveCaJobId = caJobId || resolvedCaJobId

  // ── Derived dynamic data
  const dynamicTree    = buildBrowserTree(eblResults)
  const dynamicMetrics = buildMetrics(eblResults)

  const projectName = project?.name || 'Project'

  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  const toggleGroup = (id) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedTreeItem = (() => {
    for (const node of dynamicTree) {
      if (node.id === selectedItem) return node
      if (node.children) {
        const child = node.children.find(c => c.id === selectedItem)
        if (child) return child
      }
    }
    return dynamicTree[0]
  })()

  // ── Export Business Logic ─────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (!eblResults || exportBusy) return
    setExportBusy(true)
    const appName = project?.name || 'app'
    const safe    = appName.replace(/[^a-z0-9_-]/gi, '_')

    try {
      if (exportFormat === 'markdown') {
        // ZIP containing all 3 markdown documents
        const files = buildMarkdownFiles(eblResults, appName)
        const zip   = new JSZip()
        files.forEach(f => zip.file(f.name, f.content))
        const blob = await zip.generateAsync({ type: 'blob' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = `${safe}_business_logic.zip`
        a.click()
        URL.revokeObjectURL(url)
      } else if (exportFormat === 'html') {
        // Single HTML file containing all sections
        const files = buildMarkdownFiles(eblResults, appName)
        const body  = files.map(f =>
          `<section>\n<pre style="white-space:pre-wrap;font-family:inherit">${f.content.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>\n</section>`
        ).join('\n<hr/>\n')
        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${appName} — Business Logic Export</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 960px; margin: 40px auto; padding: 0 24px; color: #1f2937; }
  h1,h2,h3 { color: #111827; } table { border-collapse: collapse; width: 100%; margin: 16px 0; }
  th,td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; } th { background: #f9fafb; }
  pre { background: #f3f4f6; padding: 16px; border-radius: 8px; overflow-x: auto; }
  hr { border: none; border-top: 2px solid #e5e7eb; margin: 40px 0; }
</style>
</head>
<body>
<h1>${appName} — Business Logic Export</h1>
<p><em>Generated by RecodeIT · ${new Date().toISOString().slice(0,10)}</em></p>
${body}
</body>
</html>`
        _triggerDownload(html, `${safe}_business_logic.html`, 'text/html')
      }
    } catch (err) {
      console.error('[EBL Export] Failed:', err)
    } finally {
      setExportBusy(false)
      setShowExport(false)
    }
  }, [eblResults, exportFormat, exportBusy, project])

  // ── Generate Business Logic — Phase 1 button wiring ───────────────────────
  const handleGenerate = useCallback(async () => {
    if (!effectiveCaJobId || eblLoading) return
    setEblLoading(true)
    setEblError(null)
    setEblResults(null)
    setEblProgress('queued')

    try {
      // Resolve user_id the same way GuidedAnalyzeView does
      const user = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()
      const userId = user?.user_id || user?.id || ''

      const job = await startExtractBizLogic({
        scoutAccountId: accountId || project?.account || '',
        applicationName: project?.name || '',
        userId,
        caJobId: effectiveCaJobId,
        scope,
        includeSpec,
        includeRules,
        includeTxGroups,
        includeGrounding,
      })

      setEblProgress('running')

      const results = await waitForEBL(job.job_id, {
        intervalMs: 3000,
        timeoutMs:  180_000,
        onProgress: (s) => setEblProgress(s),
      })

      setEblResults(results)
      setEblProgress('completed')
      setView('workbench')
    } catch (err) {
      setEblError(err.message || 'EBL extraction failed. Check API logs.')
      setEblProgress('')
    } finally {
      setEblLoading(false)
    }
  }, [effectiveCaJobId, accountId, project, scope, includeSpec, includeRules, includeTxGroups, includeGrounding, eblLoading])

  // ── HEADER ─────────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className="runs-header-bar">
      <div className="runs-header-left">
        <button className="runs-back-btn" onClick={onBack}>
          <ArrowLeft size={15} />
          <span>Runs</span>
        </button>
        <div className="runs-breadcrumb">
          <span className="runs-bc-project">{projectName}</span>
          <ChevronRight size={14} className="runs-bc-sep" />
          <span className="runs-bc-page">Extract Business Logic</span>
        </div>
      </div>

      <div className="runs-header-center">
        <div className="mode-switcher">
          <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
            <Home size={16} /><span>Home</span>
          </button>
          <button className="mode-btn" onClick={() => onGoToAnalyze?.()} title="Analyze">
            <BarChart2 size={16} /><span>Analyze</span>
          </button>
          {/* Workflow and MCP hidden — accessible via Journey dropdown
          <button className="mode-btn" disabled title="Workflow">
            <Workflow size={16} /><span>Workflow</span>
          </button>
          <button className="mode-btn" disabled title="MCP — coming soon">
            <Plug size={16} /><span>MCP</span>
          </button>
          */}
          <button className="mode-btn" onClick={() => onOpenReports?.()} title="Reports">
            <FileBarChart size={16} /><span>Reports</span>
          </button>

          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              disabled={false}
              title="HITL Journey"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToGenerateDocs?.() }}>
                    <span>Generate Documentation</span>
                  </button>
                  <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                    <span>Extract Business Logic</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToDecomposeCode?.() }}><span>Define Services</span></button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}><span>Build Services</span></button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}><span>Refactor Code</span></button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}><span>Refine Code</span></button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}><span>Build / Package</span></button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="runs-header-right">
        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help">
          <HelpCircle size={18} />
        </button>
        <div className="user-menu-container">
          <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
            <div className="user-avatar"><User size={16} /></div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>
          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || ''}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                <Settings size={16} /><span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                <Home size={16} /><span>Back to Runs</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ── SETUP SCREEN ───────────────────────────────────────────────────────────

  const renderSetup = () => (
    <div className="ebl-setup-body">

      {/* Main config area */}
      <div className="ebl-setup-main">
        <div className="ebl-setup-header">
          <h2 className="ebl-setup-title">Extract Business Logic</h2>
          <p className="ebl-setup-subtitle">
            Generate source-grounded business specifications from analyzed COBOL artifacts
            for <strong>{projectName}</strong>.
          </p>
          <div className="ebl-source-chip">
            <FolderOpen size={13} />
            <span>{projectName}</span>
            <span className="ebl-chip-sep">·</span>
            <span className={`ebl-chip-status ${effectiveCaJobId ? 'ready' : caJobResolving ? 'warn' : 'warn'}`}>
              {effectiveCaJobId
                ? `CA ${effectiveCaJobId.slice(0, 8)}… · Ready`
                : caJobResolving
                  ? 'Looking up CA job…'
                  : 'No CA job linked'}
            </span>
          </div>
        </div>

        {!effectiveCaJobId && !caJobResolving && (
          <div className="ebl-warn-banner">
            <AlertCircle size={15} />
            <span>
              No Code Analysis job is linked. Run{' '}
              <button className="ebl-warn-link" onClick={onGoToAnalyze}>Analyze first</button>.
            </span>
          </div>
        )}

        {/* Error banner */}
        {eblError && (
          <div className="ebl-warn-banner" style={{ borderColor: '#ef4444', background: 'rgba(239,68,68,0.08)' }}>
            <AlertCircle size={15} style={{ color: '#ef4444' }} />
            <span style={{ color: '#fca5a5' }}>{eblError}</span>
            <button
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              onClick={() => setEblError(null)}
            >
              <X size={13} />
            </button>
          </div>
        )}

        {/* Loading state */}
        {eblLoading && (
          <div className="ebl-warn-banner" style={{ borderColor: 'var(--accent-color, #6366f1)', background: 'rgba(99,102,241,0.08)' }}>
            <Loader size={15} className="ebl-spin" style={{ color: 'var(--accent-color, #6366f1)', animation: 'spin 1s linear infinite' }} />
            <span style={{ color: 'var(--text-secondary, #d1d5db)' }}>
              {eblProgress === 'queued'  && 'Job queued — waiting for worker…'}
              {eblProgress === 'running' && 'Extracting business logic…'}
              {(!eblProgress || eblProgress === 'starting') && 'Starting extraction…'}
            </span>
          </div>
        )}

        {/* Scope */}
        <div className="ebl-setup-section">
          <h3 className="ebl-setup-section-label">Scope</h3>
          <div className="ebl-choice-row">
            {[
              { id: 'all',      label: 'Entire Application', desc: 'All analyzed programs and files'  },
              { id: 'selected', label: 'Selected Programs',  desc: 'Choose specific programs or files' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`ebl-choice-card ${scope === opt.id ? 'selected' : ''}`}
                onClick={() => setScope(opt.id)}
                disabled={eblLoading}
              >
                <div className="ebl-choice-radio">
                  {scope === opt.id ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                </div>
                <div className="ebl-choice-text">
                  <span className="ebl-choice-name">{opt.label}</span>
                  <span className="ebl-choice-desc">{opt.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Business Logic View */}
        <div className="ebl-setup-section">
          <h3 className="ebl-setup-section-label">Business Logic View</h3>
          <div className="ebl-choice-row">
            {[
              { id: 'app',  label: 'Application Level', desc: 'Domains, flows, and high-level spec'  },
              { id: 'file', label: 'File Level',         desc: 'Per-program business logic breakdown'  },
              { id: 'both', label: 'Both',               desc: 'Full application and file-level output' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`ebl-choice-card ${bizView === opt.id ? 'selected' : ''}`}
                onClick={() => setBizView(opt.id)}
                disabled={eblLoading}
              >
                <div className="ebl-choice-radio">
                  {bizView === opt.id ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                </div>
                <div className="ebl-choice-text">
                  <span className="ebl-choice-name">{opt.label}</span>
                  <span className="ebl-choice-desc">{opt.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Inclusion options */}
        <div className="ebl-setup-section">
          <h3 className="ebl-setup-section-label">Include in Output</h3>
          <div className="ebl-options-group">
            {[
              { key: 'spec',     label: 'Detailed Functional Specification', desc: 'Full functional breakdown per program or domain', val: includeSpec,      set: setIncludeSpec      },
              { key: 'rules',    label: 'Business Rules',                     desc: 'Extracted rules mapped to source paragraphs',    val: includeRules,     set: setIncludeRules     },
              { key: 'txgroups', label: 'Transaction Grouping',               desc: 'Group related operations into transaction flows', val: includeTxGroups,  set: setIncludeTxGroups  },
              { key: 'ground',   label: 'Source Grounding',                   desc: 'Link every claim back to the source program',    val: includeGrounding, set: setIncludeGrounding },
            ].map((opt, i) => (
              <div key={opt.key} className={`ebl-option-row${i > 0 ? ' border-top' : ''}`}>
                <div className="ebl-option-text">
                  <span className="ebl-option-label">{opt.label}</span>
                  <span className="ebl-option-desc">{opt.desc}</span>
                </div>
                <button
                  className={`ebl-toggle-btn ${opt.val ? 'on' : ''}`}
                  onClick={() => opt.set(v => !v)}
                  disabled={eblLoading}
                >
                  {opt.val ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="ebl-setup-actions">
          <button className="ebl-btn-secondary" onClick={onBack} disabled={eblLoading}>Back</button>
          <button
            className={`ebl-btn-primary ${(!effectiveCaJobId || eblLoading || caJobResolving) ? 'disabled' : ''}`}
            disabled={!effectiveCaJobId || eblLoading || caJobResolving}
            onClick={handleGenerate}
            title={!effectiveCaJobId ? 'Run Analyze first to generate business logic' : undefined}
          >
            {eblLoading
              ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />{' '}Extracting…</>
              : <><Play size={15} />Generate Business Logic</>
            }
          </button>
        </div>
      </div>

      {/* Right summary card */}
      <aside className="ebl-setup-summary">
        <p className="ebl-summary-heading">EXTRACTION PLAN</p>

        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Project</span>
          <span className="ebl-summary-value">{projectName}</span>
        </div>
        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Scope</span>
          <span className="ebl-summary-value">
            {scope === 'all' ? 'Entire Application' : 'Selected Programs'}
          </span>
        </div>
        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Business logic view</span>
          <span className="ebl-summary-value">
            {bizView === 'app' ? 'Application Level' : bizView === 'file' ? 'File Level' : 'Application + File'}
          </span>
        </div>

        <div className="ebl-summary-divider" />

        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Functional spec</span>
          <span className={`ebl-summary-value ${includeSpec ? 'on' : 'off'}`}>
            {includeSpec ? 'Included' : 'Excluded'}
          </span>
        </div>
        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Business rules</span>
          <span className={`ebl-summary-value ${includeRules ? 'on' : 'off'}`}>
            {includeRules ? 'Included' : 'Excluded'}
          </span>
        </div>
        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Transaction grouping</span>
          <span className={`ebl-summary-value ${includeTxGroups ? 'on' : 'off'}`}>
            {includeTxGroups ? 'Included' : 'Excluded'}
          </span>
        </div>
        <div className="ebl-summary-block">
          <span className="ebl-summary-label">Source grounding</span>
          <span className={`ebl-summary-value ${includeGrounding ? 'on' : 'off'}`}>
            {includeGrounding ? 'Included' : 'Excluded'}
          </span>
        </div>

        <div className="ebl-summary-divider" />
        <p className="ebl-summary-note">
          Business logic is extracted from Code Analysis artifacts. Run Analyze to refresh source data.
        </p>
      </aside>
    </div>
  )

  // ── WORKBENCH TOOLBAR ──────────────────────────────────────────────────────

  const renderWorkbenchToolbar = () => (
    <div className="ebl-toolbar">
      <div className="ebl-toolbar-left">
        <button className="ebl-toolbar-back" onClick={() => setView('setup')}>
          <ArrowLeft size={14} />
          <span>Setup</span>
        </button>
        <div className="ebl-toolbar-sep" />
        <div className="ebl-tab-group">
          {[
            { id: 'browse',      label: 'Browse',          icon: List       },
            { id: 'application', label: 'Application View', icon: Building2  },
            { id: 'file',        label: 'File View',        icon: FileText   },
          ].map(tab => (
            <button
              key={tab.id}
              className={`ebl-tab ${workbenchTab === tab.id ? 'active' : ''}`}
              onClick={() => setWorkbenchTab(tab.id)}
            >
              <tab.icon size={14} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="ebl-toolbar-right">
        {eblResults && (
          <span style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckCircle2 size={12} style={{ color: '#10b981' }} />
            {eblResults.rule_count} rules · {eblResults.program_count} program{eblResults.program_count !== 1 ? 's' : ''}
          </span>
        )}
        <button className="ebl-toolbar-btn" onClick={() => { setView('setup'); setEblResults(null) }}>
          <RefreshCw size={13} />
          <span>Regenerate</span>
        </button>
        <button
          className={`ebl-toolbar-btn primary ${showExport ? 'active' : ''}`}
          onClick={() => setShowExport(v => !v)}
        >
          <Download size={13} />
          <span>Export</span>
          <ChevronDown size={11} />
        </button>
      </div>
    </div>
  )

  // ── BROWSER (left column) ──────────────────────────────────────────────────

  const renderBrowser = () => (
    <aside className="ebl-browser">
      <div className="ebl-browser-header">
        <span className="ebl-browser-title">Business Logic Browser</span>
      </div>

      <div className="ebl-browser-search-row">
        <Search size={13} className="ebl-browser-search-icon" />
        <input
          className="ebl-browser-search"
          placeholder="Search…"
          value={browserSearch}
          onChange={e => setBrowserSearch(e.target.value)}
        />
      </div>

      <div className="ebl-browser-tree">
        {dynamicTree.map(node => {
          if (node.children === null) {
            const Icon = node.icon
            return (
              <button
                key={node.id}
                className={`ebl-tree-node ebl-tree-leaf ${selectedItem === node.id ? 'selected' : ''}`}
                onClick={() => setSelectedItem(node.id)}
              >
                <div className="ebl-tree-node-icon"><Icon size={14} /></div>
                <span className="ebl-tree-node-label">{node.label}</span>
              </button>
            )
          }

          const GroupIcon = node.icon
          const isExpanded = expandedGroups.has(node.id)
          return (
            <div key={node.id} className="ebl-tree-group">
              <button
                className="ebl-tree-group-header"
                onClick={() => toggleGroup(node.id)}
              >
                <div className="ebl-tree-node-icon"><GroupIcon size={14} /></div>
                <span className="ebl-tree-group-label">{node.label}</span>
                {node.count !== undefined && node.count > 0 && (
                  <span className="ebl-tree-count">{node.count}</span>
                )}
                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>

              {isExpanded && (
                <div className="ebl-tree-children">
                  {node.children.length > 0
                    ? node.children.map(child => {
                        const ChildIcon = child.icon
                        return (
                          <button
                            key={child.id}
                            className={`ebl-tree-node ebl-tree-child ${selectedItem === child.id ? 'selected' : ''}`}
                            onClick={() => setSelectedItem(child.id)}
                          >
                            <div className="ebl-tree-node-icon"><ChildIcon size={13} /></div>
                            <span className="ebl-tree-node-label">{child.label}</span>
                          </button>
                        )
                      })
                    : (
                      <div className="ebl-tree-empty">
                        {eblResults ? 'None identified' : 'Run extraction to populate'}
                      </div>
                    )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </aside>
  )

  // ── SPEC VIEW (center column) ──────────────────────────────────────────────

  const renderSpecView = () => {
    const item = selectedTreeItem
    const typeLabels = {
      application: 'Application',
      domain:      'Business Domain',
      group:       'Group',
      program:     'Program',
    }

    return (
      <div className="ebl-spec">
        {/* Spec document header */}
        <div className="ebl-spec-header">
          <div className="ebl-spec-header-left">
            <h3 className="ebl-spec-title">{item?.label || 'Select an item'}</h3>
            <div className="ebl-spec-badges">
              <span className={`ebl-type-badge ebl-type-${item?.type || 'application'}`}>
                {typeLabels[item?.type] || item?.type}
              </span>
              {eblResults && (
                <span className="ebl-ai-badge">
                  <Sparkles size={11} />
                  AI-generated
                </span>
              )}
            </div>
          </div>
          <div className="ebl-spec-header-actions">
            <button className="ebl-spec-action-btn">
              <Download size={13} />
              Export
            </button>
            <button className="ebl-spec-action-btn" onClick={() => { setView('setup'); setEblResults(null) }}>
              <RefreshCw size={13} />
              Regenerate
            </button>
          </div>
        </div>

        {/* Spec section list */}
        <div className="ebl-spec-body">
          {SPEC_SECTIONS.map(sec => {
            const SectionIcon = sec.icon
            const isOpen = expandedSections.has(sec.id)
            const content = getSpecContent(sec.id, eblResults)
            const hasContent = content !== null && content !== undefined

            return (
              <div key={sec.id} className="ebl-spec-section">
                <button
                  className={`ebl-spec-section-header ${isOpen ? 'open' : ''}`}
                  onClick={() => toggleSection(sec.id)}
                >
                  <div className="ebl-spec-section-icon"><SectionIcon size={13} /></div>
                  <span className="ebl-spec-section-title">{sec.label}</span>
                  {hasContent && <span style={{ fontSize: 10, color: '#10b981', marginLeft: 4 }}>●</span>}
                  {isOpen ? <ChevronUp size={13} className="ebl-sec-chevron" /> : <ChevronDown size={13} className="ebl-sec-chevron" />}
                </button>
                {isOpen && (
                  <div className="ebl-spec-section-body">
                    {hasContent
                      ? renderSectionContent(sec.id, content)
                      : (
                        <div className="ebl-spec-placeholder">
                          {eblResults
                            ? 'No data available for this section.'
                            : `${sec.label} will appear here after extraction runs.`}
                        </div>
                      )
                    }
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── SOURCE CONTEXT (right column) ─────────────────────────────────────────

  const renderSourceContext = () => {
    const specs    = eblResults?.program_specs || {}
    const catalog  = eblResults?.business_rules_catalog || {}
    const files    = specs.files_accessed   || []
    const copybooks= specs.copybooks        || []
    const called   = specs.called_programs  || []
    const grounding= specs.source_grounding || {}
    const ruleCount= catalog.rules?.length  || 0
    const confAvg  = catalog.rules?.length
      ? (catalog.rules.reduce((s, r) => s + (r.confidence || 0), 0) / catalog.rules.length)
      : 0

    return (
      <aside className="ebl-context">
        <div className="ebl-context-header">
          <span className="ebl-context-title">Source Context</span>
        </div>

        <div className="ebl-context-body">
          {/* Grounding indicator */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Grounding Confidence</span>
            <div className="ebl-grounding-bar">
              <div className="ebl-grounding-fill" style={{ width: eblResults ? `${Math.round(confAvg * 100)}%` : '0%' }} />
            </div>
            <span className="ebl-grounding-note">
              {eblResults ? `${Math.round(confAvg * 100)}% avg confidence across ${ruleCount} rules` : 'Run extraction to see confidence'}
            </span>
          </div>

          {/* Source files */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Source Files</span>
            {files.length > 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {files.map((f, i) => (
                    <span key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary, #d1d5db)' }}>
                      {typeof f === 'string' ? f : f.name || '—'}
                    </span>
                  ))}
                </div>
              : <div className="ebl-context-list-empty">{eblResults ? 'None identified' : 'Run extraction to see source files'}</div>
            }
          </div>

          {/* Paragraphs / sections */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Paragraphs Analyzed</span>
            {grounding.paragraph_count != null
              ? <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #e5e7eb)' }}>{grounding.paragraph_count}</span>
              : <div className="ebl-context-list-empty">—</div>
            }
          </div>

          {/* Datasets */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Data Fields</span>
            {grounding.data_field_count != null
              ? <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #e5e7eb)' }}>{grounding.data_field_count}</span>
              : <div className="ebl-context-list-empty">—</div>
            }
          </div>

          {/* Copybooks */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Copybooks</span>
            {copybooks.length > 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {copybooks.map((c, i) => (
                    <span key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary, #d1d5db)' }}>{c}</span>
                  ))}
                </div>
              : <div className="ebl-context-list-empty">{eblResults ? 'None' : '—'}</div>
            }
          </div>

          {/* Related programs */}
          <div className="ebl-context-block">
            <span className="ebl-context-label">Called Programs</span>
            {called.length > 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {called.map((p, i) => (
                    <span key={i} style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-secondary, #d1d5db)' }}>{p}</span>
                  ))}
                </div>
              : <div className="ebl-context-list-empty">{eblResults ? 'None' : '—'}</div>
            }
          </div>

          {/* Quick links */}
          <div className="ebl-context-divider" />
          <div className="ebl-context-actions">
            <button className="ebl-ctx-action-btn" disabled>
              <ExternalLink size={13} />
              Open Source
            </button>
            <button className="ebl-ctx-action-btn" disabled>
              <FileText size={13} />
              View Technical Doc
            </button>
            <button className="ebl-ctx-action-btn" disabled>
              <Download size={13} />
              Export Item
            </button>
          </div>
        </div>
      </aside>
    )
  }

  // ── APPLICATION-LEVEL VIEW ─────────────────────────────────────────────────

  const renderApplicationView = () => {
    const overview  = eblResults?.application_overview || {}
    const specs     = eblResults?.program_specs        || {}
    const catalog   = eblResults?.business_rules_catalog || {}
    const rulesByType = catalog.rule_type_summary || {}

    const appSections = [
      {
        title: 'Application Overview',
        content: overview.summary || specs.business_purpose,
        render: (c) => <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary, #e5e7eb)' }}>{c}</p>,
      },
      {
        title: 'Business Domains',
        content: overview.domains_identified?.length ? overview.domains_identified : null,
        render: (c) => (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {c.map((d, i) => (
              <span key={i} style={{ fontSize: 12, padding: '4px 10px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 20, color: '#a5b4fc' }}>
                {typeof d === 'string' ? d : d.name}
              </span>
            ))}
          </div>
        ),
      },
      {
        title: 'Key End-to-End Flows',
        content: overview.transaction_flows?.length ? overview.transaction_flows : null,
        render: (c) => renderFlowsContent(c),
      },
      {
        title: 'Business Rules Summary',
        content: Object.keys(rulesByType).length ? rulesByType : null,
        render: (c) => (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            {Object.entries(c).filter(([,v]) => v > 0).map(([type, count]) => (
              <div key={type} style={{ padding: '8px 10px', background: 'var(--surface-2, rgba(255,255,255,0.04))', borderRadius: 6, border: `1px solid ${(RULE_TYPE_COLORS[type] || '#6b7280')}33` }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: RULE_TYPE_COLORS[type] || '#9ca3af' }}>{count}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}>{type}</div>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: 'External Dependencies',
        content: overview.external_integrations?.length ? overview.external_integrations : null,
        render: (c) => renderListContent(c),
      },
      {
        title: 'Risks / Ambiguities',
        content: specs.risks?.length ? specs.risks : null,
        render: (c) => renderListContent(c),
      },
    ]

    return (
      <div className="ebl-appview">
        <div className="ebl-appview-header">
          <h3 className="ebl-appview-title">Application Business Overview</h3>
          <p className="ebl-appview-subtitle">
            High-level business view for <strong>{projectName}</strong>.
            {!eblResults && ' Run extraction to populate.'}
          </p>
        </div>

        {/* Summary metrics — real data */}
        <div className="ebl-metrics-strip">
          {dynamicMetrics.map(m => {
            const Icon = m.icon
            return (
              <div key={m.label} className="ebl-metric-card">
                <div className="ebl-metric-icon"><Icon size={16} /></div>
                <div className="ebl-metric-body">
                  <span className="ebl-metric-value">{m.value}</span>
                  <span className="ebl-metric-label">{m.label}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Content sections */}
        {appSections.map(({ title, content, render }) => (
          <div key={title} className="ebl-appview-section">
            <h4 className="ebl-appview-section-title">{title}</h4>
            <div className="ebl-appview-section-body">
              {content
                ? render(content)
                : (
                  <div className="ebl-spec-placeholder">
                    {eblResults ? 'No data available.' : `${title} will appear after extraction runs.`}
                  </div>
                )
              }
            </div>
          </div>
        ))}
      </div>
    )
  }

  // ── FILE-LEVEL VIEW ────────────────────────────────────────────────────────

  const renderFileView = () => {
    const specs    = eblResults?.program_specs || {}
    const catalog  = eblResults?.business_rules_catalog || {}
    const programs = eblResults?.application_overview?.programs_analyzed || []

    const fileSections = [
      {
        label: 'Business Purpose',
        icon: Zap,
        content: specs.business_purpose,
        render: (c) => renderTextContent(c),
      },
      {
        label: 'Functional Steps',
        icon: List,
        content: specs.functional_steps?.length ? specs.functional_steps : null,
        render: (c) => renderListContent(c),
      },
      {
        label: 'Business Rules',
        icon: BookOpen,
        content: catalog.rules?.length ? catalog.rules : null,
        render: (c) => renderRulesContent(c),
      },
      {
        label: 'Data Touched',
        icon: Database,
        content: specs.source_grounding?.data_field_count != null
          ? `${specs.source_grounding.data_field_count} data fields analyzed`
          : null,
        render: (c) => renderTextContent(c),
      },
      {
        label: 'Files / Datasets Read/Written',
        icon: FolderOpen,
        content: specs.files_accessed?.length ? specs.files_accessed : null,
        render: (c) => renderFilesContent(c),
      },
      {
        label: 'Called Programs',
        icon: GitBranch,
        content: specs.called_programs?.length ? specs.called_programs : null,
        render: (c) => renderListContent(c),
      },
      {
        label: 'Transaction Grouping',
        icon: Tag,
        content: specs.functional_groups?.length ? specs.functional_groups : null,
        render: (c) => renderFlowsContent(c),
      },
      {
        label: 'Source Grounding',
        icon: Link,
        content: specs.source_grounding && Object.keys(specs.source_grounding).length ? specs.source_grounding : null,
        render: (c) => renderGroundingContent(c),
      },
    ]

    return (
      <div className="ebl-fileview">
        <div className="ebl-fileview-header">
          <h3 className="ebl-fileview-title">File / Program-Level Business Logic</h3>
          <p className="ebl-fileview-subtitle">
            {eblResults
              ? `Business logic extracted from ${programs.length || 1} program(s).`
              : 'Run extraction to populate program-level data.'}
          </p>
        </div>

        <div className="ebl-fileview-body">
          {/* Program selector */}
          <div className="ebl-fileview-selector">
            <span className="ebl-fileview-selector-label">Program</span>
            {programs.length > 0
              ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {programs.map((p, i) => (
                    <span key={i} style={{ fontSize: 12, padding: '3px 10px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 4, color: '#6ee7b7', fontFamily: 'monospace' }}>
                      {typeof p === 'string' ? p : p.name}
                    </span>
                  ))}
                </div>
              : <div className="ebl-fileview-selector-empty">
                  {eblResults ? 'No programs found' : 'No programs available — run extraction first'}
                </div>
            }
          </div>

          {/* File-level spec sections */}
          {fileSections.map(sec => {
            const Icon = sec.icon
            return (
              <div key={sec.label} className="ebl-fileview-section">
                <div className="ebl-fileview-section-header">
                  <Icon size={14} />
                  <span>{sec.label}</span>
                </div>
                <div className="ebl-fileview-section-body">
                  {sec.content
                    ? sec.render(sec.content)
                    : (
                      <div className="ebl-spec-placeholder">
                        {eblResults ? 'No data available.' : `${sec.label} data will appear after extraction runs.`}
                      </div>
                    )
                  }
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── EXPORT PANEL ───────────────────────────────────────────────────────────

  const renderExportPanel = () => {
    const appName = project?.name || 'app'
    const safe    = appName.replace(/[^a-z0-9_-]/gi, '_')

    // What will be downloaded for the selected format
    const downloadInfo = {
      markdown: { filename: `${safe}_business_logic.zip`, label: 'ZIP · 3 Markdown files', supported: true  },
      html:     { filename: `${safe}_business_logic.html`, label: 'Single HTML file',       supported: true  },
      pdf:      { filename: null,                           label: 'PDF — Coming soon',      supported: false },
    }
    const info = downloadInfo[exportFormat] || downloadInfo.markdown

    // Files included in the markdown/html export
    const exportFiles = buildMarkdownFiles(eblResults, appName)

    return (
      <div className="ebl-export-backdrop" onClick={() => setShowExport(false)}>
        <div className="ebl-export-panel" onClick={e => e.stopPropagation()}>
          <div className="ebl-export-header">
            <span className="ebl-export-title">Export Business Logic</span>
            <button className="ebl-export-close" onClick={() => setShowExport(false)}>
              <X size={15} />
            </button>
          </div>

          <div className="ebl-export-body">
            {/* Format selector */}
            <div className="ebl-export-section">
              <h4 className="ebl-export-section-label">Output Format</h4>
              <div className="ebl-export-formats">
                {[
                  { id: 'markdown', label: 'MARKDOWN', supported: true  },
                  { id: 'html',     label: 'HTML',     supported: true  },
                  { id: 'pdf',      label: 'PDF',      supported: false },
                ].map(fmt => (
                  <button
                    key={fmt.id}
                    className={`ebl-format-btn ${exportFormat === fmt.id ? 'selected' : ''} ${!fmt.supported ? 'disabled' : ''}`}
                    onClick={() => fmt.supported && setExportFormat(fmt.id)}
                    disabled={!fmt.supported}
                    title={!fmt.supported ? 'PDF export coming soon' : undefined}
                  >
                    {fmt.label}
                    {!fmt.supported && (
                      <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.6 }}>soon</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* What will be downloaded */}
            <div className="ebl-export-section">
              <h4 className="ebl-export-section-label">Package Contents</h4>
              <div className="ebl-export-file-list">
                {exportFiles.map(f => (
                  <div key={f.name} className="ebl-export-file-row">
                    <div className="ebl-export-file-type md">MD</div>
                    <div className="ebl-export-file-meta">
                      <span className="ebl-export-file-name">{f.name}</span>
                      <span className="ebl-export-file-desc">{f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Download filename preview */}
            <div className="ebl-export-section" style={{ paddingBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 6 }}>
                <Download size={13} style={{ color: '#10b981', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary, #e5e7eb)', fontFamily: 'monospace' }}>
                    {info.filename || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', marginTop: 1 }}>{info.label}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer action */}
          <div className="ebl-export-footer">
            <button
              className="ebl-btn-secondary"
              onClick={() => setShowExport(false)}
              disabled={exportBusy}
            >
              Cancel
            </button>
            <button
              className={`ebl-btn-primary ${(!info.supported || exportBusy) ? 'disabled' : ''}`}
              disabled={!info.supported || exportBusy || !eblResults}
              onClick={handleExport}
            >
              {exportBusy
                ? <><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Exporting…</>
                : <><Download size={13} /> Download</>
              }
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── WORKBENCH ──────────────────────────────────────────────────────────────

  const renderWorkbench = () => (
    <div className="ebl-workbench">
      {renderWorkbenchToolbar()}

      <div className="ebl-workbench-body">
        {workbenchTab === 'browse' && (
          <>
            {renderBrowser()}
            {renderSpecView()}
            {renderSourceContext()}
          </>
        )}
        {workbenchTab === 'application' && renderApplicationView()}
        {workbenchTab === 'file'        && renderFileView()}
      </div>

      {showExport && renderExportPanel()}
    </div>
  )

  // ── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div className="ebl-view">
      {renderHeader()}
      {view === 'setup' ? renderSetup() : renderWorkbench()}
    </div>
  )
}

export default ExtractBizLogicView
